import { getParty, PARTIES } from '@val/shared'
import { useMemo } from 'react'
import { int } from '../lib/format.ts'
import { assignSeats, hemicycleLayout } from './hemicycleLayout.ts'
import './hemicycle.css'

interface Props {
  seatsByParty: Record<string, number>
  totalSeats: number
  majority: number
  highlightParty?: string
  /** Extra stort läge för kioskskärm. */
  large?: boolean
  /** Rubrik i mitten, t.ex. koalitionssumma. */
  centerLabel?: { value: string; caption: string }
}

const SEAT_ORDER = [...PARTIES].sort((a, b) => a.order - b.order).map((p) => p.code)

export function Hemicycle({
  seatsByParty,
  totalSeats,
  majority,
  highlightParty,
  large = false,
  centerLabel,
}: Props) {
  const width = large ? 940 : 720
  const geo = useMemo(() => hemicycleLayout(totalSeats, width), [totalSeats, width])

  // Ta med ev. okända partier sist i ordningen.
  const order = useMemo(() => {
    const known = new Set(SEAT_ORDER)
    const extra = Object.keys(seatsByParty).filter(
      (c) => !known.has(c) && (seatsByParty[c] ?? 0) > 0,
    )
    return [...SEAT_ORDER, ...extra.sort()]
  }, [seatsByParty])

  const filled = useMemo(
    () => assignSeats(geo.seats.length, seatsByParty, order),
    [geo.seats.length, seatsByParty, order],
  )

  const totalPlaced = Object.values(seatsByParty).reduce((a, b) => a + b, 0)

  return (
    <svg
      className="hemicycle"
      viewBox={`0 -22 ${geo.width} ${geo.height + 28}`}
      role="img"
      aria-label={`Mandatfördelning: ${totalPlaced} av ${totalSeats} platser`}
    >
      <title>Mandatfördelning i Ale kommunfullmäktige</title>

      {/* majoritetsmarkör – linje uppåt från centrum */}
      <line
        x1={geo.cx}
        y1={geo.cy - geo.outerRadius - 2}
        x2={geo.cx}
        y2={geo.cy - geo.innerRadius + 6}
        className="hemicycle__majority-line"
      />
      <text x={geo.cx} y={geo.cy - geo.outerRadius - 8} className="hemicycle__majority-label">
        majoritet {majority}
      </text>

      {geo.seats.map((s, i) => {
        const code = filled[i]
        const p = code ? getParty(code) : null
        const isHi = code != null && code === highlightParty
        return (
          <circle
            key={`${s.row}-${s.x.toFixed(1)}-${s.y.toFixed(1)}`}
            cx={s.x}
            cy={s.y}
            r={geo.seatRadius * (isHi ? 1.02 : 1)}
            fill={p ? p.color : 'var(--surface-2)'}
            className={`hemicycle__seat${isHi ? ' is-highlight' : ''}${code ? '' : ' is-empty'}`}
          >
            {p ? <title>{p.name}</title> : null}
          </circle>
        )
      })}

      {centerLabel ? (
        <g className="hemicycle__center">
          <text x={geo.cx} y={geo.cy - 26} className="hemicycle__center-value">
            {centerLabel.value}
          </text>
          <text x={geo.cx} y={geo.cy - 6} className="hemicycle__center-caption">
            {centerLabel.caption}
          </text>
        </g>
      ) : (
        <g className="hemicycle__center">
          <text x={geo.cx} y={geo.cy - 24} className="hemicycle__center-value">
            {int(totalPlaced)}
          </text>
          <text x={geo.cx} y={geo.cy - 4} className="hemicycle__center-caption">
            av {totalSeats} mandat
          </text>
        </g>
      )}
    </svg>
  )
}
